import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../ui/Button';
import { CheckCircleIcon, EnvelopeIcon } from '@heroicons/react/24/solid';
import { getAuth } from 'firebase/auth';

/**
 * Componente que exibe a tela de sucesso após a conclusão do setup
 * Inclui próximos passos recomendados e um tour guiado inicial
 */
const SuccessStep = ({ salonId, businessName, verificationSent = false, isEmailVerified = false, sendVerificationEmail, checkEmailVerification }) => {
  const navigate = useNavigate();
  const [emailVerified, setEmailVerified] = useState(isEmailVerified);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(verificationSent);
  
  // Verificar status de verificação de e-mail periodicamente
  useEffect(() => {
    if (!emailVerified) {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        const checkInterval = setInterval(async () => {
          setCheckingVerification(true);
          
          try {
            const isVerified = await checkEmailVerification(user);
            
            if (isVerified) {
              setEmailVerified(true);
              clearInterval(checkInterval);
            }
          } catch (error) {
            console.error('Erro ao verificar email:', error);
          } finally {
            setCheckingVerification(false);
          }
        }, 10000); // Verificar a cada 10 segundos
        
        return () => clearInterval(checkInterval);
      }
    }
  }, [emailVerified, checkEmailVerification]);
  
  // Próximos passos recomendados para o novo proprietário
  const nextSteps = [
    {
      id: 'complete-profile',
      title: 'Complete o perfil do seu negócio',
      description: 'Adicione logotipo, fotos e informações detalhadas sobre seu salão',
      icon: '🏢',
      route: `/${salonId}/admin/settings/profile`
    },
    {
      id: 'invite-staff',
      title: 'Convide sua equipe',
      description: 'Adicione todos os profissionais para gerenciar suas agendas',
      icon: '👥',
      route: `/${salonId}/admin/professionals`
    },
    {
      id: 'customize-services',
      title: 'Personalize seus serviços',
      description: 'Revise e ajuste os serviços que você oferece e seus preços',
      icon: '✂️',
      route: `/${salonId}/admin/services`
    },
    {
      id: 'setup-booking',
      title: 'Configure sua página de agendamentos',
      description: 'Personalize como seus clientes verão sua página de agendamentos',
      icon: '📅',
      route: `/${salonId}/admin/settings/booking`
    }
  ];

  const handleGoToAdmin = () => {
    navigate(`/${salonId}/admin`);
  };
  
  const handleResendVerification = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (user && sendVerificationEmail) {
      await sendVerificationEmail(user);
      setEmailVerificationSent(true);
    }
  };

  return (
    <div className="animate-fadeIn text-center">
      <div className="flex flex-col items-center justify-center mb-8">
        <div className="bg-green-100 p-3 rounded-full">
          <CheckCircleIcon className="h-12 w-12 text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mt-4">
          Parabéns! Seu negócio foi criado com sucesso
        </h2>
        <p className="text-gray-600 mt-2 max-w-md mx-auto">
          {businessName} agora está pronto para receber agendamentos. Siga os próximos passos para personalizar sua experiência.
        </p>
      </div>
      
      {/* Seção de verificação de e-mail */}
      {!emailVerified && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 max-w-lg mx-auto">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <EnvelopeIcon className="h-6 w-6 text-blue-500" />
            </div>
            <div className="ml-3 text-left">
              <h3 className="text-base font-medium text-blue-800">Verifique seu e-mail</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>Enviamos um link de verificação para o seu e-mail. Por favor, verifique sua caixa de entrada e clique no link para confirmar sua conta.</p>
                
                {checkingVerification ? (
                  <div className="mt-2 flex items-center text-sm">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Verificando status...
                  </div>
                ) : (
                  <div className="mt-3">
                    <button 
                      onClick={handleResendVerification}
                      disabled={emailVerificationSent}
                      className={`text-sm font-medium ${
                        emailVerificationSent 
                          ? 'text-blue-400 cursor-default' 
                          : 'text-blue-600 hover:text-blue-800'
                      }`}
                    >
                      {emailVerificationSent 
                        ? 'E-mail de verificação enviado' 
                        : 'Reenviar e-mail de verificação'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-gray-200 pt-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">
          Próximos passos recomendados
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {nextSteps.map((step) => (
            <div 
              key={step.id}
              className="bg-white border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all rounded-lg p-4 flex items-start text-left"
            >
              <span className="text-2xl mr-3">{step.icon}</span>
              <div>
                <h4 className="font-medium text-gray-800">{step.title}</h4>
                <p className="text-sm text-gray-600">{step.description}</p>
                <Link 
                  to={step.route} 
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium mt-2 inline-block"
                >
                  Configurar →
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 text-left">
          <h4 className="font-medium text-blue-800 flex items-center">
            <span className="text-xl mr-2">💡</span>
            Dica profissional
          </h4>
          <p className="text-sm text-blue-700">
            Compartilhe sua página de agendamentos com os clientes através das redes sociais para começar a receber reservas imediatamente.
          </p>
          <div className="mt-3 text-sm bg-white rounded border border-blue-100 p-2 text-gray-600 break-all">
            {window.location.origin}/{salonId}
          </div>
        </div>
      </div>

      <div className="space-x-4">
        <Button
          onClick={handleGoToAdmin}
          size="lg"
        >
          Ir para o Painel de Controle
        </Button>
      </div>
    </div>
  );
};

export default SuccessStep; 